import { defineComponent as _defineComponent } from 'vue'

enum ModalState {
  Expanded = 'Expanded',
  Docked = 'Docked',
  Minimized = 'Minimized',
}

const MINIMIZED_MODAL_MAX_WIDTH = 164
const MINIMIZED_MODAL_HEIGHT = 40
const DOCKED_MODAL_WIDTH = 404
const EXPANDED_MODAL_WIDTH = 540
const WIDE_EXPANDED_MODAL_WIDTH = 828

export {
  DOCKED_MODAL_WIDTH,
  EXPANDED_MODAL_WIDTH,
  MINIMIZED_MODAL_HEIGHT,
  MINIMIZED_MODAL_MAX_WIDTH,
  ModalState,
  WIDE_EXPANDED_MODAL_WIDTH,
}

import { toCssCustomProperties } from '@@/bits/css'
import OzFocusTrap from '@@/library/v4/components/OzFocusTrap.vue'
import { usePreferredReducedMotion } from '@vueuse/core'
import { kebabCase, uniqueId } from 'lodash-es'
import { computed, nextTick, ref, watch } from 'vue'

type ExcludeIdenticalStates<T extends string> = T extends `${infer A}_To_${infer B}` ? (A extends B ? never : T) : never

type StateTransitionKey = ExcludeIdenticalStates<`${ModalState}_To_${ModalState}`>
type ValidTransitionKey = StateTransitionKey | `${ModalState}Enter` | `${ModalState}Leave`


export default /*#__PURE__*/_defineComponent({
  __name: 'DockableModal',
  props: {
    id: null,
    state: null,
    isMarkedForClosure: { type: Boolean },
    dir: null,
    isPhone: { type: Boolean },
    isWide: { type: Boolean },
    modalStates: null,
    containerWidth: null,
    initialEndOffset: null,
    gapBetweenModalsInDock: null
  },
  emits: ["close"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'close'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  id: string
  state: ModalState
  isMarkedForClosure: boolean
  dir: 'ltr' | 'rtl'
  isPhone: boolean
  isWide: boolean
  modalStates: { id: string; state: ModalState }[]
  containerWidth: number
  initialEndOffset: number
  gapBetweenModalsInDock: number
};

/**
 * A dockable modal is a modal that can be docked to the bottom end corner of the container.
 * On phone layout, the modal takes up the whole container.
 *
 * A dockable modal has 3 states:
 * - Minimized: shown as a small bar. The content of this state is provided by the `minimized-content` slot.
 * - Docked: expands up from the 'minimized' bar to show more content (provided by the `main-content` slot).
 * - Expanded: shown at the center of the container. Displays the same content as the 'docked' state.
 *
 * This component doesn't define the appearance of the modal. It only handles the positioning and transitions.
 *
 * IMPORTANT: This component is not meant to be used directly. Use `DockableModalList` instead.
 */




/** End offset is the distance from the modal to the end edge of the available space. */
const endOffset = ref(props.initialEndOffset)

/**
 * Custom CSS properties to use in the `<template>` & `<style>` blocks.
 */
const customCssProperties = computed(() => {
  return toCssCustomProperties(
    {
      endOffset: props.dir === 'ltr' ? -endOffset.value : endOffset.value,
      translateToDock: 'translate(var(--end-offset), calc(var(--bottom-padding) * -1))',
    },
    { numericUnit: 'px' },
  )
})

const displayedState = computed(() => (props.isPhone ? ModalState.Expanded : props.state))

// ------------------------------------------------------------------------------
// MODAL TRANSITION
//
// We implement our own transition system to have more control over the lifecycle
// and how the classes are applied to the elements.
// ------------------------------------------------------------------------------

/**
 * Type function to exclude transition keys of the same states e.g. `Docked_To_Docked`.
 */
const rootEl = ref<Element>()

const transitionClasses = ref<string[]>([])
const isTransitionEnded = ref(false)
const transitionKey = ref<ValidTransitionKey>()

/**
 * Resolves once the DOM has been updated.
 */
const awaitDOMUpdates = async () => {
  await nextTick()
  await new Promise((resolve) => window.requestAnimationFrame(resolve))
}

/**
 * Resolves once all performed transitions have finished.
 */
const awaitTransitionEnd = async () => {
  await awaitDOMUpdates()
  // Steal from https://x.com/argyleink/status/1793679490222055709
  // Old Android browsers, like 6 and 7, don't support getAnimations
  await Promise.allSettled(
    typeof rootEl.value?.getAnimations === 'function'
      ? rootEl.value.getAnimations({ subtree: true }).map((animation) => animation.finished)
      : [],
  )
}

/**
 * Returns the space that a modal of the specified state would take in the dock.
 * Space = width + gap
 */
const getModalSpaceInDock = (state: ModalState, numOfDockedModals: number): number => {
  // Expanded modal doesn't take any space in dock.
  if (state === ModalState.Expanded) return 0
  // Docked modal takes a fixed space.
  if (state === ModalState.Docked) return DOCKED_MODAL_WIDTH + props.gapBetweenModalsInDock
  // Minimized modal space is divided equally after subtracting the space taken by docked modals.
  const availableSpaceForMinimizedModals =
    props.containerWidth -
    props.initialEndOffset -
    numOfDockedModals * (DOCKED_MODAL_WIDTH + props.gapBetweenModalsInDock)
  const minimizedModalSpace = availableSpaceForMinimizedModals / (props.modalStates.length - numOfDockedModals)
  return Math.min(minimizedModalSpace, MINIMIZED_MODAL_MAX_WIDTH + props.gapBetweenModalsInDock)
}

/**
 * Calculates the end offset of the current modal if it was in the specified state.
 * Used in `beforeTransition` hook to set the correct end offset for the transition.
 */
const updateEndOffset = (state: ModalState.Minimized | ModalState.Docked) => {
  // Create a new array of modal states with the current modal's state updated.
  const modalIndex = props.modalStates.findIndex((m) => m.id === props.id)
  const newModals = props.modalStates.map((m, i) => {
    if (i === modalIndex) return { ...m, state }
    return m
  })
  const numOfDockedModals = newModals.filter((m) => m.state === ModalState.Docked).length

  // Current modal's offset is the sum of all previous modals' offsets.
  let accumulatedOffset = props.initialEndOffset
  for (let i = 0; i < newModals.length; i++) {
    const previousModal = newModals[i - 1]
    const currentModal = newModals[i]
    const offset = previousModal ? getModalSpaceInDock(previousModal.state, numOfDockedModals) : 0
    accumulatedOffset += offset
    // Stop when we reach the current modal.
    if (currentModal.id === props.id) break
  }
  endOffset.value = accumulatedOffset
}

// -------------------------------------------------------------
// TRANSITION LIFECYCLE
//
//
//        {state}-enter-from     ->>     {state}-enter-to
//                      {state}-enter-active
//
//
// {state1}-to-{state2}-from     ->>     {state1}-to-{state2}-to
//                  {state1}-to-{state2}-active
//
//
//        {state}-leave-from     ->>     {state}-leave-to
//                      {state}-leave-active
//
//
// We use a similar naming convention for the transition classes
// compared to Vue. The watcher on `props.state` takes care of
// generating the class names based on the transition state and
// adding/removing them at appropriate timings.
// -------------------------------------------------------------

const beforeTransition = () => {
  isTransitionEnded.value = false
  // End offset is used in Minimized enter/leave, Docked enter/leave,
  // Minimized < -> Expanded & Docked < -> Expanded transitions.
  // We update it before those transitions start.
  const minimizeTransitions: ValidTransitionKey[] = [
    'MinimizedEnter',
    'MinimizedLeave',
    'Minimized_To_Expanded',
    'Expanded_To_Minimized',
  ]
  const dockTransitions: ValidTransitionKey[] = [
    'DockedEnter',
    'DockedLeave',
    'Docked_To_Expanded',
    'Expanded_To_Docked',
  ]
  if (minimizeTransitions.includes(transitionKey.value!)) {
    updateEndOffset(ModalState.Minimized)
  } else if (dockTransitions.includes(transitionKey.value!)) {
    updateEndOffset(ModalState.Docked)
  }
  transitionClasses.value = [kebabCase(`${transitionKey.value}-from`)]
}

const startTransition = () => {
  transitionClasses.value = [kebabCase(`${transitionKey.value}-active`), kebabCase(`${transitionKey.value}-to`)]
}

const afterTransition = () => {
  // Clean up the transition.
  transitionClasses.value = []
  isTransitionEnded.value = true

  // If this is a leave transition, emit a `close` event
  // so it can be removed properly.
  if (transitionKey.value?.endsWith('Leave')) emit('close')
}

const prefersReducedMotion = usePreferredReducedMotion()

watch(
  [displayedState, () => props.isMarkedForClosure],
  async ([newState, isClosing], [oldState]) => {
    const isEnterTransition = oldState == null
    const isLeaveTransition = isClosing === true

    if (isEnterTransition) {
      transitionKey.value = `${newState}Enter` as const
    } else if (isLeaveTransition) {
      transitionKey.value = `${oldState}Leave` as const
    } else {
      transitionKey.value = `${oldState}_To_${newState}` as StateTransitionKey
    }

    // If user prefers reduced motion, don't transition.
    if (prefersReducedMotion.value === 'reduce') {
      afterTransition()
      return
    }

    beforeTransition()
    await awaitDOMUpdates()
    startTransition()
    await awaitTransitionEnd()
    afterTransition()
  },
  { immediate: true },
)

const focusTrapDescriptionId = uniqueId('focusTrap')

/**
 * Show the minimized content when:
 * - The modal is minimized and is entering/leaving.
 * - The modal has completed transition from docked or expanded to minimized.
 */
const xMinimizedContent = computed(() => {
  if (displayedState.value !== ModalState.Minimized) return false
  return (
    transitionKey.value === 'MinimizedEnter' ||
    transitionKey.value === 'MinimizedLeave' ||
    (transitionKey.value?.endsWith('To_Minimized') && isTransitionEnded.value)
  )
})

// -----------------------------------------
// CALCULATING ELEMENT POSITION INSIDE MODAL
// -----------------------------------------

const modalContainer = ref<Element>()

/**
 * When the modal is expanded, we apply a `transform` to center it on the screen. `transform` creates
 * a new containing block that's not the viewport, so even `position: fixed` elements will be positioned
 * relative to it. See: https://darrenlester.com/blog/why-fixed-position-element-not-relative-to-viewport
 * To get around this, we expose this method that helps calculate the position of an element relative to
 * the modal container.
 */
const getElementRectInsideModal = (element: Element): DOMRect | null => {
  const containingBlockRect =
    props.state === ModalState.Expanded
      ? modalContainer.value?.getBoundingClientRect()
      : document.documentElement.getBoundingClientRect()
  if (containingBlockRect == null) return null
  const elementRect = element.getBoundingClientRect()
  return new DOMRect(
    elementRect.left - containingBlockRect.left,
    elementRect.top - containingBlockRect.top,
    elementRect.width,
    elementRect.height,
  )
}

expose({
  getElementRectInsideModal,
})

return { __sfc: true,ModalState, MINIMIZED_MODAL_MAX_WIDTH, MINIMIZED_MODAL_HEIGHT, DOCKED_MODAL_WIDTH, EXPANDED_MODAL_WIDTH, WIDE_EXPANDED_MODAL_WIDTH, props, emit, endOffset, customCssProperties, displayedState, rootEl, transitionClasses, isTransitionEnded, transitionKey, awaitDOMUpdates, awaitTransitionEnd, getModalSpaceInDock, updateEndOffset, beforeTransition, startTransition, afterTransition, prefersReducedMotion, focusTrapDescriptionId, xMinimizedContent, modalContainer, getElementRectInsideModal, OzFocusTrap }
}

})